import { useEffect, useRef } from "react";

import { Box, Stack } from "@mui/material";
// eslint-disable-next-line no-restricted-imports
import CircularProgress from "@mui/material/CircularProgress";
import { FormProvider, UseFormReturn } from "react-hook-form";

import { useTranslate } from "../../../../../../common/hooks/useTranslate";
import { useLiveFormAmplitudeEvents } from "../../../common/amplitude/useLiveFormAmplitudeEvents";
import { FormStepContainer } from "../../FormStepContainer";
import { StepDescriptionFormData } from "../shared/types";

import { ModeTabs, DescriptionWizardMode } from "./ModeTabs/ModeTabs";
import { PromptComposer } from "./PromptComposer/PromptComposer";

import { DeleteConfirmations, deleteConfirmationAnchorSx } from "@/components/DeleteConfirmation/DeleteConfirmation";
import { LiveEditorBlock } from "@/components/LiveEditorElements/LiveEditorBlock/LiveEditorBlock";
import { OutlinedTextFieldWysiwyg } from "@/components/OutlinedTextFieldWysiwyg/OutlinedTextFieldWysiwyg";
import { FroalaConfig } from "@/components/OutlinedTextFieldWysiwyg/type";
import { DashboardAiLoader } from "@/dashboard-design-system/AiLoader";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { DescriptionWizardPhase } from "@/features/LiveFormEditor/Steps/StepDescription/StepDescriptionAiWizard/helpers/useDescriptionWizardSession";

export type LiveEditorStepDescriptionAiWizardStatelessProps = {
  form: UseFormReturn<StepDescriptionFormData>;
  wording: {
    stepTitle: string;
    stepDescription: string;
  };
  handleSaveAndNext: () => Promise<void>;
  handleSaveAndBack: () => Promise<void>;
  mode: DescriptionWizardMode;
  phase: DescriptionWizardPhase;
  isManualPrefillLoading?: boolean;
  promptValue: string;
  repromptValue: string;
  onModeChange: (mode: DescriptionWizardMode) => void;
  onPromptChange: (value: string) => void;
  onRepromptChange: (value: string) => void;
  onGenerate: () => void;
  onRegenerate: () => void;
  onDeleteDescription: () => void;
  onDescriptionChange?: (value: string) => void;
};

export const LiveEditorStepDescriptionAiWizardStateless = ({
  form,
  wording,
  handleSaveAndNext,
  handleSaveAndBack,
  mode,
  phase,
  isManualPrefillLoading = false,
  promptValue,
  repromptValue,
  onModeChange,
  onPromptChange,
  onRepromptChange,
  onGenerate,
  onRegenerate,
  onDeleteDescription,
  onDescriptionChange,
}: LiveEditorStepDescriptionAiWizardStatelessProps) => {
  const { t } = useTranslate();
  const { logUserInputEvent } = useLiveFormAmplitudeEvents();
  const { errors } = form.formState;

  const description = form.watch("description");
  const hasDescription = Boolean(description?.trim?.());
  const isGenerating = phase === "generating";
  const showGeneratedEditor = mode === "generate" && (isGenerating || phase === "generated");

  const generatedFieldRef = useRef<HTMLDivElement | null>(null);
  const previousPhaseRef = useRef(phase);
  useEffect(() => {
    if (previousPhaseRef.current === "generating" && phase === "generated") {
      generatedFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    previousPhaseRef.current = phase;
  }, [phase]);

  return (
    <FormProvider {...form}>
      <FormStepContainer
        title={wording.stepTitle}
        description={wording.stepDescription}
        form={form}
        actionsProps={{
          onBack: handleSaveAndBack,
          onNext: handleSaveAndNext,
          nextButtonLabel: hasDescription ? "next" : "skip",
          // Leaving mid-generation would unmount the wizard before its description can be saved.
          disableBack: isGenerating,
          disableNext: isGenerating,
        }}
      >
        <LiveEditorBlock rowGap={2} sx={deleteConfirmationAnchorSx}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <DashboardTypography variant="subtitle2">
              {t("dashboard", "liveFormEditor.descriptionWizard.cardTitle")}
            </DashboardTypography>
            <DeleteConfirmations confirmation={onDeleteDescription} shouldConfirm={hasDescription} />
          </Stack>

          <ModeTabs mode={mode} onModeChange={onModeChange} />

          {mode === "generate" && phase === "idle" && (
            <PromptComposer
              label={t("dashboard", "liveFormEditor.descriptionWizard.promptLabel")}
              placeholder={t("dashboard", "liveFormEditor.descriptionWizard.promptPlaceholder")}
              submitLabel={t("dashboard", "liveFormEditor.descriptionWizard.generate")}
              value={promptValue}
              showSubmitIcon
              isSubmitting={false}
              dataTest="description-wizard-prompt"
              onChange={onPromptChange}
              onSubmit={onGenerate}
            />
          )}

          {showGeneratedEditor && (
            <Stack ref={generatedFieldRef} gap={1} width="100%">
              <DashboardTypography variant="body2" vibe="text-neutral-moderate">
                {t("dashboard", "liveFormEditor.descriptionWizard.generatedDescriptionLabel")}
              </DashboardTypography>
              {isGenerating ? (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  height={283}
                  sx={(theme) => ({
                    backgroundColor: theme.palette.surface.neutral.white,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.border.brand.intense}`,
                  })}
                  data-chromatic="ignore"
                >
                  <DashboardAiLoader size="large" />
                </Stack>
              ) : (
                <Stack
                  sx={(theme) => ({
                    width: "100%",
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.border.brand.intense}`,
                    boxShadow: `0 0 0 3px ${theme.palette.border.brand.moderate}`,
                    backgroundColor: theme.palette.surface.neutral.white,
                    overflow: "hidden",
                    "& > div:first-of-type": {
                      border: "none",
                      borderRadius: 0,
                      "&:hover": { border: "none" },
                    },
                  })}
                >
                  <OutlinedTextFieldWysiwyg
                    id="description"
                    name="description"
                    keyName="description"
                    error={errors["description"]}
                    heightMinOver310={283}
                    placeholderText={t("dashboard", "donationForm.defaultMobileDescription")}
                    defaultValue={form.watch("description") || ""}
                    config={FroalaConfig.Minimalist}
                    onChange={onDescriptionChange}
                    onBlur={logUserInputEvent({
                      inputName: "description",
                      isEmpty: !form.getValues("description")?.trim?.(),
                      isValid: !errors["description"],
                    })}
                  />
                  <Box
                    sx={(theme) => ({
                      borderTop: `1px solid ${theme.palette.border.brand.intense}`,
                    })}
                  >
                    <PromptComposer
                      label={t("dashboard", "liveFormEditor.descriptionWizard.repromptLabel")}
                      optionalLabel={t("dashboard", "liveFormEditor.descriptionWizard.repromptOptional")}
                      placeholder={t("dashboard", "liveFormEditor.descriptionWizard.repromptPlaceholder")}
                      submitLabel={t("dashboard", "liveFormEditor.descriptionWizard.submitAndRegenerate")}
                      value={repromptValue}
                      minRows={3}
                      showSubmitIcon
                      embedded
                      isSubmitting={false}
                      dataTest="description-wizard-reprompt"
                      onChange={onRepromptChange}
                      onSubmit={onRegenerate}
                    />
                  </Box>
                </Stack>
              )}
            </Stack>
          )}

          {mode === "manual" && isManualPrefillLoading && (
            <Stack
              alignItems="center"
              justifyContent="center"
              height={310}
              width="100%"
              sx={(theme) => ({
                backgroundColor: theme.palette.surface.neutral.white,
                borderRadius: 1,
                border: `1px solid ${theme.palette.border.neutral.quiet}`,
              })}
              data-chromatic="ignore"
            >
              <CircularProgress size={32} />
            </Stack>
          )}

          {mode === "manual" && !isManualPrefillLoading && (
            <Box minHeight={310} position="relative" width="100%">
              <OutlinedTextFieldWysiwyg
                id="description"
                name="description"
                keyName="description"
                error={errors["description"]}
                heightMinOver310={310}
                placeholderText={t("dashboard", "donationForm.defaultMobileDescription")}
                defaultValue={form.watch("description") || ""}
                config={FroalaConfig.Minimalist}
                onChange={onDescriptionChange}
                onBlur={logUserInputEvent({
                  inputName: "description",
                  isEmpty: !form.getValues("description")?.trim?.(),
                  isValid: !errors["description"],
                })}
              />
            </Box>
          )}
        </LiveEditorBlock>
      </FormStepContainer>
    </FormProvider>
  );
};
